import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import SwiperCore, { Navigation, Pagination, SwiperOptions, Thumbs } from 'swiper';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import { ModalConfig, ModalOption } from '@shared/components/modal/modal.interface';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { UtilitiesService } from '@services/utilities.service';
import { CommonModule } from '@angular/common';
import { InlineSVGModule } from 'ng-inline-svg-2';

@Component({
  selector: 'app-image-preview',
  standalone: true,
  imports: [CommonModule, SwiperModule, InlineSVGModule, ModalComponent],
  templateUrl: './image-preview.component.html',
  styleUrls: ['./image-preview.component.scss'],
})
export class ImagePreviewComponent implements OnInit, OnChanges, AfterViewChecked {
  @Input() images: string[] | undefined;
  @Input() image: string | null;
  @Input() actionLabel: string | null;
  @Input() openTextLabel: boolean = true;
  @Input() titleModal: string | undefined | null = 'Image';
  @Input() height?: number;
  @Input() width?: number;
  @Input() borderRadius?: number;
  @Input() centerImage = true;
  @Input() useThumbnail: boolean = false;
  // Opt-in, gak ada consumer lain yang kena dampak secara default (LPJ Klaim Dokumen Nota, MAI-2247).
  @Input() imgClass?: string;

  @ViewChild('swiperCarousel') swiperCarousel: SwiperComponent;
  @ViewChild('thumbsSwiper') thumbsSwiper: SwiperComponent;
  thumbsSwiperRef: any;
  thumbsInstance: any;
  currentZoom = 1;
  currentRotation = 0;
  panX = 0;
  panY = 0;
  activeSlideIndex = 0;
  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
  imagePreviewConfig = {
    minScale: 1,
    maxScale: 5,
    rotationStep: 90,
    minRotationScale: 0,
    maxRotationScale: 360,
  };
  modalImageOptions: ModalOption = {
    fullscreen: true,
  };
  @ViewChild('modalImage') private modalImage: ModalComponent;
  @ViewChild('actionsRow') private actionsRow: ElementRef<HTMLDivElement>;
  @ViewChild('swiperWrapper') private swiperWrapper: ElementRef<HTMLDivElement>;
  private actionListenerCleanups: (() => void)[] = [];
  private attachedContentEl: HTMLElement | null = null;
  private isPanning = false;
  private panStart = { x: 0, y: 0, panX: 0, panY: 0 };

  constructor(
    private utils: UtilitiesService,
    private cdr: ChangeDetectorRef
  ) {
    SwiperCore.use([Navigation, Pagination, Thumbs]);
  }

  ngOnInit(): void {
    this.handleOneImage();
    this.initSwiperConfig();
  }

  // modalConfig.onOpened kadang gak emit — attach listener gak boleh gantung ke situ, jadi dicek
  // tiap CD cycle lewat perubahan actionsRow (null↔ada = modal buka/tutup/buka-ulang).
  ngAfterViewChecked(): void {
    const el = this.actionsRow?.nativeElement ?? null;
    if (el === this.attachedContentEl) return;
    this.detachActionListeners();
    this.attachedContentEl = el;
    if (el) {
      this.attachActionListeners();
      this.attachPanListeners();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.images && !changes.images.firstChange) {
      // console.log('image preview onchanges ', changes);
    }

    if (changes.image) {
      this.image = changes.image.currentValue;
      this.images = [];
      this.handleOneImage();
    }
  }

  handleZoom(direction: string) {
    const { minScale, maxScale } = this.imagePreviewConfig;

    if (direction === 'ZOOM_IN') {
      this.currentZoom++;
      this.currentZoom = this.currentZoom < maxScale ? this.currentZoom : maxScale;
    }

    if (direction === 'ZOOM_OUT') {
      this.currentZoom--;
      this.currentZoom = this.currentZoom > minScale ? this.currentZoom : minScale;
      if (this.currentZoom === 1) {
        this.resetTransform();
      }
    }

    return this.currentZoom;
  }

  handleRotate(direction: string) {
    const { rotationStep } = this.imagePreviewConfig;
    this.currentRotation = direction === 'ROTATE_RIGHT' ? this.currentRotation + rotationStep : this.currentRotation - rotationStep;
    return this.currentRotation;
  }

  resetValue = () => {
    this.image = null;
  };

  resetZoom = () => {
    this.currentZoom = this.imagePreviewConfig.minScale;
    this.panX = 0;
    this.panY = 0;
  };

  resetTransform = () => {
    const currentSlide = document.getElementById(`imagePreview_${this.activeSlideIndex}`);
    if (currentSlide) {
      currentSlide.style.removeProperty('transform');
    }
    this.panX = 0;
    this.panY = 0;
  };

  resetRotate = () => (this.currentRotation = 0);

  config: SwiperOptions;

  initSwiperConfig() {
    this.config = {
      spaceBetween: 50,
      navigation: true,
      // Sembunyiin nav/pagination kalau cuma 1 slide (tanpa ini arrow tetap muncul walau inert).
      watchOverflow: true,
      // Zoom module Swiper dimatikan — API-nya gak dipakai (zoom/pan kita manual via transform),
      // dan gesture handler bawaannya rebutan event sama pan manual kita.
      allowTouchMove: !this.useThumbnail,
      pagination: !this.useThumbnail ? { clickable: true } : undefined,
      on: {
        init: (e) => {
          this.activeSlideIndex = e.activeIndex;
        },
        slideChange: (e) => {
          this.resetRotate();
          this.resetZoom();
          this.activeSlideIndex = e.activeIndex;
          if (this.useThumbnail && this.thumbsInstance) {
            this.thumbsInstance.slideTo(e.activeIndex);
          }
        },
      },
    };
  }

  modalImageConfig: ModalConfig = {
    showFooter: false,
    showHeader: false,
    onClose: () => {
      this.detachActionListeners();
      this.attachedContentEl = null;
      this.resetValue();
      this.resetZoom();
      this.resetRotate();
      this.resetTransform();
      return true;
    },
  };

  // (click) di action buttons gak pernah nempel — itu content di-project ke TemplateRef yang
  // dibuka NgbModal.open(), dan Angular gak attach listener declarative ke node situ. Cuma
  // addEventListener native langsung yang terbukti jalan (dipanggil dari ngAfterViewChecked).
  private attachActionListeners(): void {
    this.detachActionListeners();
    const buttons = this.actionsRow?.nativeElement?.children;
    if (!buttons) return;
    const handlers = [() => this.handleZoom('ZOOM_IN'), () => this.handleZoom('ZOOM_OUT'), () => this.handleRotate('ROTATE_LEFT'), () => this.handleRotate('ROTATE_RIGHT')];
    Array.from(buttons).forEach((el, i) => {
      const listener = () => {
        handlers[i]();
        this.cdr.detectChanges();
        // clampPan() butuh ukuran gambar POST-zoom/rotate — baca rect setelah detectChanges
        // pertama (yang udah nge-apply transform baru), lalu render ulang kalau pan-nya kekoreksi.
        this.clampPan();
        this.cdr.detectChanges();
      };
      el.addEventListener('click', listener);
      this.actionListenerCleanups.push(() => el.removeEventListener('click', listener));
    });
  }

  private detachActionListeners(): void {
    this.actionListenerCleanups.forEach((cleanup) => cleanup());
    this.actionListenerCleanups = [];
  }

  // cdkDrag diganti pan manual (gotcha sejenis attachActionListeners). Pakai Pointer Events,
  // bukan mouse events — Swiper's core swipe-handler preventDefault() di 'pointerdown' bikin
  // browser suppress sintesis 'mousedown' sepenuhnya (dibuktikan via diagnostic log real user:
  // 1200+ 'mousemove' tapi nol 'mousedown', padahal cursor tetap jadi grabbing).
  private attachPanListeners(): void {
    const wrapper = this.swiperWrapper?.nativeElement;
    if (!wrapper) return;
    const onDragStart = (e: DragEvent) => e.preventDefault();
    const onPointerDown = (e: PointerEvent) => {
      if (this.currentZoom < 2 || !(e.target as HTMLElement).closest('.image-preview-draggable')) return;
      this.isPanning = true;
      this.panStart = { x: e.clientX, y: e.clientY, panX: this.panX, panY: this.panY };
      // Cegah Swiper ikut proses gesture yang sama (swipe antar slide) selagi kita pan gambar yang di-zoom.
      e.preventDefault();
      e.stopPropagation();
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!this.isPanning) return;
      this.panX = this.panStart.panX + (e.clientX - this.panStart.x);
      this.panY = this.panStart.panY + (e.clientY - this.panStart.y);
      this.clampPan();
      this.cdr.detectChanges();
    };
    const onPointerUp = () => (this.isPanning = false);

    wrapper.addEventListener('dragstart', onDragStart, true);
    wrapper.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerup', onPointerUp, true);
    this.actionListenerCleanups.push(() => {
      wrapper.removeEventListener('dragstart', onDragStart, true);
      wrapper.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('pointermove', onPointerMove, true);
      document.removeEventListener('pointerup', onPointerUp, true);
    });
  }

  // Batasin pan biar tepi gambar berhenti pas ketemu tepi viewport (.image-preview) — sebelum ini
  // panX/panY gak dibatasi sama sekali, jadi gambar zoom bisa digeser sampai hilang total dari
  // layar. getBoundingClientRect() dipakai karena udah otomatis merefleksikan zoom+rotasi terkini
  // (translate gak ngubah width/height rect, jadi aman dibaca kapan aja regardless posisi pan).
  private clampPan(): void {
    const draggable = document.getElementById(`imagePreview_${this.activeSlideIndex}`);
    const viewport = draggable?.closest('.image-preview');
    const img = draggable?.querySelector('img');
    if (!draggable || !viewport || !img) return;
    const imgRect = img.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    const maxPanX = Math.max(0, (imgRect.width - viewportRect.width) / 2);
    const maxPanY = Math.max(0, (imgRect.height - viewportRect.height) / 2);
    this.panX = Math.min(maxPanX, Math.max(-maxPanX, this.panX));
    this.panY = Math.min(maxPanY, Math.max(-maxPanY, this.panY));
  }

  handleOneImage() {
    if (!this.images?.length && this.image) {
      this.images = [this.image];
    }
  }

  handleAction() {
    return this.modalImage.open();
  }

  onSlideChange(e: any) {
    this.activeSlideIndex = e.activeIndex;
  }

  onThumbsInit(swiper: any) {
    if (!this.useThumbnail) return;
    this.thumbsInstance = swiper;
    setTimeout(() => {
      if (this.swiperCarousel?.swiperRef) {
        this.swiperCarousel.swiperRef.thumbs.swiper = swiper;
        this.swiperCarousel.swiperRef.thumbs.init();
        this.swiperCarousel.swiperRef.thumbs.update(true);
      }
    });
  }

  goToSlide(index: number): void {
    if (this.swiperCarousel?.swiperRef) {
      this.swiperCarousel.swiperRef.slideTo(index);
      this.activeSlideIndex = index;
      this.currentZoom = 1;
      this.currentRotation = 0;
      this.panX = 0;
      this.panY = 0;
    }
  }

  handleNoImage = (e: Event) => this.utils.setDefaultImageProduct(e);
}
